import React, { createContext, useContext, useState, useEffect } from 'react';
import zh from './locales/zh';
import en from './locales/en';

const STORAGE_KEY = 'app-language';

const translations = { zh, en };

/**
 * 检测浏览器语言
 * @returns {'zh' | 'en'}
 */
function detectBrowserLanguage() {
  const browserLang = navigator.language || navigator.userLanguage;
  // 中文优先（zh-CN, zh-TW, zh-HK 等都算中文）
  if (browserLang.startsWith('zh')) {
    return 'zh';
  }
  return 'en';
}

/**
 * 获取初始语言
 * 优先级：localStorage > 浏览器语言
 */
function getInitialLanguage() {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved && (saved === 'zh' || saved === 'en')) {
    return saved;
  }
  return detectBrowserLanguage();
}

const LanguageContext = createContext(null);

/**
 * 语言提供者组件
 */
export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(getInitialLanguage);

  // 切换语言并持久化
  const setLanguage = (lang) => {
    setLanguageState(lang);
    localStorage.setItem(STORAGE_KEY, lang);
  };

  // 翻译函数
  const t = (key, params = {}) => {
    const keys = key.split('.');
    let value = translations[language];

    for (const k of keys) {
      if (value && typeof value === 'object') {
        value = value[k];
      } else {
        value = undefined;
        break;
      }
    }

    if (typeof value !== 'string') {
      console.warn(`Translation missing for key: ${key}`);
      return key;
    }

    // 替换参数 {name} -> value
    return value.replace(/\{(\w+)\}/g, (_, paramName) => {
      return params[paramName] !== undefined ? params[paramName] : `{${paramName}}`;
    });
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

/**
 * 使用语言的 Hook
 * @returns {{ language: string, setLanguage: function, t: function }}
 */
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export default LanguageContext;
